import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import {
  addressContext,
  conversationFailedText,
  hasAddressForms,
} from 'features/conversation-preferences/address-form';
import {
  useAddressForm,
  useConversationPreferences,
} from 'features/conversation-preferences/hooks/use-conversation-preferences';
import { isPreferencesForbidden } from 'features/conversation-preferences/services/conversation-preferences-service';
import { PersonalCardForm } from './personal-card-form';
import { personalValues } from './personal-values';

type PersonalCardProps = {
  /** The account names, used while the preference has no names of its own. */
  account?: { firstName?: string | null; lastName?: string | null };
};

/**
 * "Personal": how Buddy and every agent address the user (du/Sie, salutation, names) and their
 * work context. Saved to the conversation preference, not to the account.
 */
export function PersonalCard({ account }: Readonly<PersonalCardProps>) {
  const { t, i18n } = useTranslation();
  const { data, error, isError, refetch } = useConversationPreferences();
  const addressForm = useAddressForm();
  const context = addressContext(addressForm);

  // C12: a session without preferences (e.g. a widget) gets no card at all.
  if (isPreferencesForbidden(error)) return null;

  let body: ReactNode;
  if (data) {
    body = (
      <PersonalCardForm
        initialValues={personalValues(data, account)}
        stored={data}
        addressForm={addressForm}
        showAddressForm={hasAddressForms(i18n.language)}
      />
    );
  } else if (isError) {
    body = (
      <Alert variant="destructive">
        <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
          <span>{conversationFailedText(t, addressForm)}</span>
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
            {t('RETRY', { defaultValue: 'Retry' })}
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else {
    body = (
      <div className="flex flex-col gap-4" data-testid="personal-card-loading">
        <Skeleton className="h-5 w-40" />
        <div className="grid gap-5 sm:grid-cols-2">
          {[0, 1, 2, 3].map((slot) => (
            <Skeleton key={slot} className="h-11 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <Card className="w-full border border-border rounded-lg shadow-sm bg-card">
      <CardContent className="flex flex-col gap-5 p-4 sm:p-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl text-high-emphasis font-semibold">
            {t('PROFILE_CONVERSATION_SECTION', { defaultValue: 'Personal' })}
          </h2>
          <p className="text-sm text-medium-emphasis">
            {t('PROFILE_CONVERSATION_HINT', {
              defaultValue: 'So Buddy and your agents address you the right way.',
              context,
            })}
          </p>
        </div>

        {body}
      </CardContent>
    </Card>
  );
}
